/**
 * Action Types Registry - fetches and caches admin-configured action type labels
 */
import { supabase } from '@/integrations/supabase/client';

interface ActionTypeConfig {
  action_type: string;
  label: string;
  scope: 'inbound' | 'outbound' | 'both';
  enabled: boolean;
}

class ActionTypesRegistry {
  private cache = new Map<string, ActionTypeConfig>();
  private loading = false;
  private lastFetch = 0;
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  async loadActionTypeLabels(scope: 'inbound' | 'outbound' = 'outbound'): Promise<void> {
    const now = Date.now();
    
    // Skip if recently fetched
    if (this.loading || (now - this.lastFetch < this.CACHE_DURATION && this.cache.size > 0)) {
      return;
    }

    this.loading = true;
    
    try {
      const { data, error } = await supabase
        .from('admin_action_types')
        .select('action_type, label, scope, enabled')
        .eq('enabled', true)
        .or(`scope.eq.${scope},scope.eq.both`);

      if (error) throw error;

      // Clear and rebuild cache
      this.cache.clear();
      (data || []).forEach(config => {
        // Type cast the scope since Supabase may return null for existing records
        const typedConfig: ActionTypeConfig = {
          ...config,
          scope: (config.scope as 'inbound' | 'outbound' | 'both') || 'both'
        };
        this.cache.set(config.action_type, typedConfig);
      });

      this.lastFetch = now;
    } catch (error) {
      console.error('Failed to load action type labels:', error);
    } finally {
      this.loading = false;
    }
  }

  getActionLabel(actionType: string): string {
    const config = this.cache.get(actionType);
    return config?.label || this.getDefaultLabel(actionType);
  }

  private getDefaultLabel(actionType: string): string {
    const defaults: Record<string, string> = {
      'follow_up_call': 'follow-up call',
      'consent_callback': 'Callback consent given', 
      'email_provided': 'Requested email info',
      'email_promise': 'Requested email info',
      'appointment_booked': 'appointment',
      'appointment_interest': 'appointment',
      'message_taking': 'message relay'
    };
    
    return defaults[actionType] || actionType.replace(/_/g, ' ');
  }

  getAllCachedLabels(): Record<string, string> {
    const labels: Record<string, string> = {};
    this.cache.forEach((config, actionType) => {
      labels[actionType] = config.label;
    });
    return labels;
  }
}

// Singleton instance
export const actionTypesRegistry = new ActionTypesRegistry();

// Convenience function for loading outbound labels
export const loadActionTypeLabels = (scope: 'inbound' | 'outbound' = 'outbound') => 
  actionTypesRegistry.loadActionTypeLabels(scope);

// Convenience function for getting labels
export const getActionTypeLabel = (actionType: string) => 
  actionTypesRegistry.getActionLabel(actionType);